import React from "react";
import dynamic from "next/dynamic";
import cn from "classnames";
import { Id } from "storefront/lib/Id";
import useMediaQuery, {
  LARGER_THAN_TABLET,
} from "storefront/hooks/useMediaQuery";
import useSellerNotifications from "storefront/hooks/useSellerNotifications";
import usePendingOffers from "storefront/hooks/usePendingOffers";
import useAnalytics from "storefront/hooks/useAnalytics";
import navigationClicked from "storefront/Analytics/EventCreators/navigationClicked";
import { useAccountHealthStatus } from "storefront/hooks/Users/AccountHealth/useAccountHealthStatus";
import useHasExpiredListings from "storefront/hooks/useHasExpiredListings";

import styles from "./Profile.module.scss";

const MobileProfile = dynamic(() => import("./components/MobileProfile"));
const DesktopProfile = dynamic(() => import("./components/DesktopProfile"));

type ProfileMenuContextType = {
  profileMenuOpen: boolean;
  onOpenChange: (open: boolean) => void;
  avatarUrl: string;
  userId: Id;
  username: string;
};

const ProfileMenuContext = React.createContext<
  ProfileMenuContextType | undefined
>(undefined);

export const useProfileMenu = () => {
  const context = React.useContext(ProfileMenuContext);
  if (!context) {
    throw new Error("useProfileMenu must be used within a ProfileMenuProvider");
  }
  return context;
};

type ProfileProps = {
  userId: Id;
  username: string;
  avatarUrl?: string;
};

const fallbackAvatarUrl =
  "https://media-assets.grailed.com/prd/misc/PVEFHQYACL92VZTZETOTVQNFENXZW7S5U0MLUG";

const Profile = ({ avatarUrl, userId, username }: ProfileProps) => {
  const [profileMenuOpen, setProfileMenuOpen] = React.useState(false);
  const isDesktop = useMediaQuery(LARGER_THAN_TABLET);
  const { track } = useAnalytics();
  const sellerNotificationsResource = useSellerNotifications(userId);
  const { offersResource } = usePendingOffers(userId);
  const { hasViewed: hasViewedAccountHealth } = useAccountHealthStatus();
  const hasExpiredListings = useHasExpiredListings();

  const onOpenChange = (open: boolean) => {
    if (open) {
      track(navigationClicked("menu_open", "global_header"));
    }

    if (!open) {
      track(navigationClicked("menu_close", "global_header"));
    }
    setProfileMenuOpen(open);
  };

  return (
    <ProfileMenuContext.Provider
      value={{
        profileMenuOpen,
        onOpenChange,
        avatarUrl: avatarUrl ?? fallbackAvatarUrl,
        userId,
        username,
      }}
    >
      <div
        className={cn(styles.avatar, {
          [styles.notified]:
            (sellerNotificationsResource.type === "Completed" &&
              sellerNotificationsResource.value.anyUnread) ||
            (offersResource.type === "Completed" &&
              offersResource.value.length > 0) ||
            !hasViewedAccountHealth ||
            hasExpiredListings,
        })}
      >
        {isDesktop !== null && isDesktop ? <DesktopProfile /> : null}
        {isDesktop !== null && !isDesktop ? <MobileProfile /> : null}
      </div>
    </ProfileMenuContext.Provider>
  );
};

export default Profile;
